import { useEffect, useState } from 'react';
import axios from 'axios';
import { FRANCHISEE_BUSINESS_TYPES } from '../config/franchiseeBusinessTypes';
import { getApiBaseUrl } from '../services/httpClient';
import vocabulary from '../config/categorySearchVocabulary.json';

const withLocalNames = (row) => {
  const key = row.value || row.category_key || '';
  const local = vocabulary[key] || vocabulary[key.split('__').pop()] || {};
  return { ...row, teluguName: row.teluguName ?? row.telugu_name ?? local.telugu_name ?? '',
    searchAliases: row.searchAliases ?? row.search_aliases ?? local.search_aliases ?? [] };
};
const fallbackOptions = FRANCHISEE_BUSINESS_TYPES.map(withLocalNames);

const mapRowsToOptions = (rows) =>
  rows.map((row) => withLocalNames({
    ...row,
    value: row.value || row.category_key,
    label: row.label,
    group: row.group || row.category_group,
    template: row.template || row.feature_template,
    mainCategoryId: row.mainCategoryId || row.main_category_id,
    subCategoryId: row.subCategoryId || row.sub_category_id,
    thumbnailUrl: row.thumbnailUrl || row.thumbnail_url || null,
  }));

/** Prefer CRA dev proxy on localhost so category list matches admin DB (no CORS / wrong port). */
const getPublicCategoriesUrl = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${window.location.origin}/api/franchisee/public/business-categories`;
    }
  }
  return `${getApiBaseUrl()}/franchisee/public/business-categories`;
};

export default function useBusinessCategoryOptions() {
  const [options, setOptions] = useState(fallbackOptions);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadedFromApi, setLoadedFromApi] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(getPublicCategoriesUrl(), {
          params: { _t: Date.now() },
          timeout: 45000,
        });
        if (cancelled) return;
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length) {
          setOptions(mapRowsToOptions(res.data.data));
          setLoadedFromApi(true);
          setError('');
        } else {
          setLoadedFromApi(false);
          setError(res.data?.message || 'Category index unavailable — showing built-in list');
          setOptions(fallbackOptions);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadedFromApi(false);
          setError(
            err?.response?.data?.message ||
              'Could not reach category index — showing built-in list only'
          );
          setOptions(fallbackOptions);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { options, loading, error, loadedFromApi };
};
